import React, { useState, useRef, useEffect, Component, useMemo, useCallback } from "react";
import "./SchemePage.css";
import VADDetector from "../api/vad";
import processingMusic from "../assests/processing_music.mp3";
import {
  voiceTranslate,
  OtherLanguageAsr,
  translateVoice,
  asrSarvam,
  translateSarvam,
  ttsSarvam,
  ttsBani,
} from "../api/voiceApi";
import { getSchemes } from "../api/schemeApi";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  (process.env.NODE_ENV === "development" ? "http://localhost:4345" : "");



class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: "" };
  }

  static getDerivedStateFromError(err) {
    return { hasError: true, errorMessage: err?.message || "Unknown error" };
  }

  componentDidCatch(err, info) {
    console.error("[ErrorBoundary]", err, info);
  }

  handleReset = () => {
    try {
      sessionStorage.removeItem("vr_session_id");
    } catch (_) { }
    this.setState({ hasError: false, errorMessage: "" });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            gap: "16px",
            fontFamily: "sans-serif",
            padding: "24px",
            textAlign: "center",
          }}
        >
          <span style={{ fontSize: "48px" }}></span>
          <h2 style={{ margin: 0 }}>Something went wrong</h2>
          <p style={{ color: "#666", maxWidth: "480px" }}>
            {this.state.errorMessage}
          </p>
          <button
            onClick={this.handleReset}
            style={{
              padding: "10px 24px",
              background: "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "15px",
            }}
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const isDev = process.env.NODE_ENV === "development";
const log = (...a) => isDev && console.log(...a);
const warn = (...a) => isDev && console.warn(...a);
const err = (...a) => console.error(...a);

const MAX_TEXT_LENGTH = 2000;

function sanitizeText(raw) {
  if (typeof raw !== "string") return "";
  return raw
    .replace(/<[^>]*>/g, "")
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .replace(/[\u200B-\u200F\u202A-\u202E\uFEFF]/g, "")
    .trim()
    .slice(0, MAX_TEXT_LENGTH);
}

const SPEAK_AGAIN_MAP = {
  "en": "Please speak again, I could not understand your audio.",
  "en-IN": "Please speak again, I could not understand your audio.",
  "hi": "कृपया फिर से बोलें, मुझे आपकी आवाज़ समझ नहीं आई।",
  "hi-IN": "कृपया फिर से बोलें, मुझे आपकी आवाज़ समझ नहीं आई।",
  "te": "దయచేసి మళ్లీ మాట్లాడండి, మీ మాటలు నాకు అర్థం కాలేదు.",
  "te-IN": "దయచేసి మళ్లీ మాట్లాడండి, మీ మాటలు నాకు అర్థం కాలేదు.",
  "or": "ଦୟାକରି ପୁଣି ଥରେ କୁହନ୍ତୁ, ମୁଁ ଆପଣଙ୍କ କଥା ବୁଝିପାରିଲି ନାହିଁ ।",
  "or-IN": "ଦୟାକରି ପୁଣି ଥରେ କୁହନ୍ତୁ, ମୁଁ ଆପଣଙ୍କ କଥା ବୁଝିପାରିଲି ନାହିଁ ।",
  "od-IN": "ଦୟାକରି ପୁଣି ଥରେ କୁହନ୍ତୁ, ମୁଁ ଆପଣଙ୍କ କଥା ବୁଝିପାରିଲି ନାହିଁ ।",
};

const WELCOME_MESSAGE_MAP = {
  "en": "Hello, I am Scheme Saarthi, your guide to Odisha Government Schemes. How can I help you today?",
  "en-IN": "Hello, I am Scheme Saarthi, your guide to Odisha Government Schemes. How can I help you today?",

  "hi": "नमस्ते, मैं स्कीम सारथी हूँ, ओडिशा सरकार की योजनाओं के बारे में आपका मार्गदर्शक। आज मैं आपकी कैसे सहायता कर सकता हूँ?",
  "hi-IN": "नमस्ते, मैं स्कीम सारथी हूँ, ओडिशा सरकार की योजनाओं के बारे में आपका मार्गदर्शक। आज मैं आपकी कैसे सहायता कर सकता हूँ?",

  "te": "హలో, నేను స్కీమ్ సారథిని, ఒడిశా ప్రభుత్వ పథకాల కోసం మీ మార్గదర్శిని. ఈ రోజు నేను మీకు ఎలా సహాయపడగలను?",
  "te-IN": "హలో, నేను స్కీమ్ సారథిని, ఒడిశా ప్రభుత్వ పథకాల కోసం మీ మార్గదర్శిని. ఈ రోజు నేను మీకు ఎలా సహాయపడగలను?",

  "or": "ନମସ୍କାର, ମୁଁ ସ୍କିମ୍ ସାରଥୀ, ଓଡ଼ିଶା ସରକାରଙ୍କ ଯୋଜନାଗୁଡ଼ିକ ପାଇଁ ଆପଣଙ୍କର ମାର୍ଗଦର୍ଶକ। ଆଜି ମୁଁ ଆପଣଙ୍କୁ କିପରି ସାହାଯ୍ୟ କରିପାରିବି?",
  "or-IN": "ନମସ୍କାର, ମୁଁ ସ୍କିମ୍ ସାରଥୀ, ଓଡ଼ିଶା ସରକାରଙ୍କ ଯୋଜନାଗୁଡ଼ିକ ପାଇଁ ଆପଣଙ୍କର ମାର୍ଗଦର୍ଶକ। ଆଜି ମୁଁ ଆପଣଙ୍କୁ କିପରି ସାହାଯ୍ୟ କରିପାରିବି?",

  "od-IN": "ନମସ୍କାର, ମୁଁ ସ୍କିମ୍ ସାରଥୀ, ଓଡ଼ିଶା ସରକାରଙ୍କ ଯୋଜନାଗୁଡ଼ିକ ପାଇଁ ଆପଣଙ୍କର ମାର୍ଗଦର୍ଶକ। ଆଜି ମୁଁ ଆପଣଙ୍କୁ କିପରି ସାହାଯ୍ୟ କରିପାରିବି?"
};

const ENDING_TEXTS = {
  "en": "Thank you for using Scheme Saarthi. Have a great day! Feel free to return anytime you have questions about Odisha Government Schemes.",
  "en-IN": "Thank you for using Scheme Saarthi. Have a great day! Feel free to return anytime you have questions about Odisha Government Schemes.",
  "hi": "स्कीम सारथी का उपयोग करने के लिए धन्यवाद। आपका दिन शुभ हो! ओडिशा सरकार की योजनाओं के बारे में कोई भी प्रश्न होने पर आप कभी भी वापस आ सकते हैं।",
  "hi-IN": "स्कीम सारथी का उपयोग करने के लिए धन्यवाद। आपका दिन शुभ हो! ओडिशा सरकार की योजनाओं के बारे में कोई भी प्रश्न होने पर आप कभी भी वापस आ सकते हैं।",
  "te": "స్కీమ్ సారథిని ఉపయోగించినందుకు ధన్యవాదాలు. మీ రోజు శుభంగా గడవాలి! ఒడిశా ప్రభుత్వ పథకాల గురించి మీకు ఎప్పుడైనా ప్రశ్నలు ఉంటే, మళ్లీ ఎప్పుడైనా రావచ్చు.",
  "te-IN": "స్కీమ్ సారథిని ఉపయోగించినందుకు ధన్యవాదాలు. మీ రోజు శుభంగా గడవాలి! ఒడిశా ప్రభుత్వ పథకాల గురించి మీకు ఎప్పుడైనా ప్రశ్నలు ఉంటే, మళ్లీ ఎప్పుడైనా రావచ్చు.",
  "or": "ସ୍କିମ୍ ସାରଥୀ ବ୍ୟବହାର କରିଥିବାରୁ ଧନ୍ୟବାଦ। ଆପଣଙ୍କ ଦିନଟି ଶୁଭ ହେଉ! ଓଡ଼ିଶା ସରକାରଙ୍କ ଯୋଜନା ବିଷୟରେ କୌଣସି ପ୍ରଶ୍ନ ଥିଲେ, ଆପଣ ଯେକୌଣସି ସମୟରେ ପୁଣି ଆସିପାରିବେ।",
  "or-IN": "ସ୍କିମ୍ ସାରଥୀ ବ୍ୟବହାର କରିଥିବାରୁ ଧନ୍ୟବାଦ। ଆପଣଙ୍କ ଦିନଟି ଶୁଭ ହେଉ! ଓଡ଼ିଶା ସରକାରଙ୍କ ଯୋଜନା ବିଷୟରେ କୌଣସି ପ୍ରଶ୍ନ ଥିଲେ, ଆପଣ ଯେକୌଣସି ସମୟରେ ପୁଣି ଆସିପାରିବେ।",
  "od-IN": "ସ୍କିମ୍ ସାରଥୀ ବ୍ୟବହାର କରିଥିବାରୁ ଧନ୍ୟବାଦ। ଆପଣଙ୍କ ଦିନଟି ଶୁଭ ହେଉ! ଓଡ଼ିଶା ସରକାରଙ୍କ ଯୋଜନା ବିଷୟରେ କୌଣସି ପ୍ରଶ୍ନ ଥିଲେ, ଆପଣ ଯେକୌଣସି ସମୟରେ ପୁଣି ଆସିପାରିବେ।"
};

function generateUUID() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const a = new Uint8Array(16);
  crypto.getRandomValues(a);
  a[6] = (a[6] & 0x0f) | 0x40;
  a[8] = (a[8] & 0x3f) | 0x80;
  return Array.from(a, (b) => b.toString(16).padStart(2, "0"))
    .join("")
    .replace(/(.{8})(.{4})(.{4})(.{4})(.{12})/, "$1-$2-$3-$4-$5");
}

function createAudioUrlFromBase64(base64Data) {
  const chars = atob(base64Data);
  const bytes = new Uint8Array(chars.length);
  for (let i = 0; i < chars.length; i++) bytes[i] = chars.charCodeAt(i);

  let mimeType = "audio/mp3";
  if (bytes.length >= 4) {
    if (bytes[0] === 0x49 && bytes[1] === 0x44 && bytes[2] === 0x33) mimeType = "audio/mp3"; // ID3
    else if (bytes[0] === 0xFF && (bytes[1] & 0xE0) === 0xE0) mimeType = "audio/mp3"; // ADTS MP3
    else if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46) mimeType = "audio/wav"; // RIFF (WAV)
    else if (bytes.length >= 8 && bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70) mimeType = "audio/mp4"; // ftyp (MP4/AAC)
    else if (bytes[0] === 0x1A && bytes[1] === 0x45 && bytes[2] === 0xDF && bytes[3] === 0xA3) mimeType = "audio/webm"; // EBML (WebM)
    else if (bytes[0] === 0x4F && bytes[1] === 0x67 && bytes[2] === 0x67 && bytes[3] === 0x53) mimeType = "audio/ogg"; // OggS
  }

  return URL.createObjectURL(new Blob([bytes], { type: mimeType }));
}

function revokeAudioUrl(url) {
  try {
    if (url) URL.revokeObjectURL(url);
  } catch (_) { }
}

async function executeFetchAttempt(
  url,
  options,
  isValidResponse,
  attempt,
  maxRetries,
  timeoutMs
) {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    log(`fetchWithRetry: attempt ${attempt}/${maxRetries} → ${url}`);
    const res = await fetch(url, { ...options, signal: ctrl.signal });
    clearTimeout(id);
    if (!res.ok) {
      warn(`fetchWithRetry: HTTP ${res.status} on attempt ${attempt}`);
      return { success: false, retry: attempt < maxRetries };
    }
    const data = await res.json().catch(() => null);
    if (!data || !isValidResponse(data)) {
      warn(`fetchWithRetry: invalid response on attempt ${attempt}`, data);
      return { success: false, retry: attempt < maxRetries };
    }
    return { success: true, data };
  } catch (e) {
    clearTimeout(id);
    warn(`fetchWithRetry: ${e.name === "AbortError" ? "timeout" : "error"} on attempt ${attempt}`, e);
    return { success: false, retry: attempt < maxRetries };
  }
}

async function fetchWithRetry(
  url,
  options,
  isValidResponse,
  maxRetries = 3,
  delayMs = 1000,
  timeoutMs = 60000
) {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    const result = await executeFetchAttempt(
      url,
      options,
      isValidResponse,
      attempt,
      maxRetries,
      timeoutMs
    );
    if (result.success) return result.data;
    if (result.retry) await new Promise((r) => setTimeout(r, delayMs));
  }
  err(`fetchWithRetry: all ${maxRetries} attempts failed for ${url}`);
  return null;
}

const isAskResponseValid = (data) => {
  const ask = data?.ask || data;
  return !!(
    ask?.original_text ||
    ask?.sarvam_text ||
    ask?.bhashini_text ||
    ask?.english_answer ||
    ask?.answer ||
    ask?.final_answer ||
    ask?.text ||
    ask?.message ||
    ask?.response ||
    ask?.sarvam_audio_base64 ||
    ask?.bhashini_audio_base64 ||
    ask?.audio_base64
  );
};

// Always pass English question to the API; userLang is used only for display/TTS
// Always pass English question to the API; userLang is used only for display/TTS
async function callSchemeAgent(englishQuestion, sessionId, userLang, model) {
  try {
    const data = await getSchemes(englishQuestion, sessionId, userLang, model);
    if (data?.error) {
      throw new Error(data.error || "Unknown scheme agent error");
    }
    return data;
  } catch (e) {
    err("callSchemeAgent failed:", e);
    return null;
  }
}

async function fetchSchemeWelcomeMessage(userLang, sessionId, model) {
  try {
    const data = await getSchemes("Hello", sessionId, userLang, model);
    if (data?.error) {
      console.warn("Error fetching welcome message:", data.error);
      return null;
    }
    const translated = await translateSchemeAgentResponse(data, userLang);
    return {
      text: translated?.answer || null,
      audioBase64: translated?.audio_base_64 || translated?.audio_base64 || null
    };
  } catch (error) {
    console.error("Error fetching welcome message:", error.message);
    return null;
  }
}

async function translateSchemeAgentResponse(data, targetLang) {
  if (!data) return data;

  // If the backend already translated the response, assign it to data.answer
  if (data.translated_answer) {
    data.answer = data.translated_answer;
    return data;
  }

  if (!targetLang || targetLang === "en" || targetLang === "en-IN") {
    return data;
  }

  const englishAnswer = data?.answer || "";
  if (!englishAnswer) return data;

  try {
    const translated = await translateSarvam(englishAnswer, targetLang, "en");
    const translatedText =
      translated?.translated_text ||
      translated?.translation ||
      translated?.text ||
      translated?.transcript ||
      englishAnswer;
    data.answer = translatedText;
  } catch (e) {
    warn("translateSchemeAgentResponse failed:", e);
  }

  return data;
}

async function callTranslationWithRetry(formData, normalizedLang) {
  const result = await translateVoice(formData, normalizedLang);
  log("callTranslationWithRetry result:", result);
  if (result?.error) { err("Translation error:", result.error); return result; }
  return result;
}

async function validateWebMBlob(blob) {
  const buf = await blob.slice(0, 4).arrayBuffer();
  const h = new Uint8Array(buf);
  return h[0] === 0x1a && h[1] === 0x45 && h[2] === 0xdf && h[3] === 0xa3;
}

function extractTranscriptText(d) {
  if (!d) return "";
  if (d?.translation) {
    if (typeof d.translation === "string") return d.translation;
    if (typeof d.translation === "object") {
      return (
        d.translation.translation ||
        d.translation.translated_text ||
        d.translation.text ||
        d.translation.transcript ||
        d.translation.asr ||
        ""
      );
    }
  }
  return d?.transcript || d?.asr || d?.text || "";
}

async function fetchSarvamTranslation(audioBlob, normalizedLang) {
  log("Calling Sarvam ASR, blob size:", audioBlob.size, "lang:", normalizedLang);
  const asrRes = await asrSarvam(audioBlob, normalizedLang);
  const asrText = asrRes?.transcript || asrRes?.asr || (typeof asrRes === "string" ? asrRes : "");
  if (!asrText) {
    return { error: "Empty ASR", emptyASR: true };
  }
  if (normalizedLang === "en" || normalizedLang === "en-IN") {
    return { translation: { asr: asrText, translation: asrText } };
  }

  const transRes = await translateSarvam(asrText, "en", normalizedLang);
  const translatedText =
    transRes?.translation ||
    transRes?.translated_text ||
    transRes?.text ||
    transRes?.transcript ||
    "";

  return {
    translation: {
      asr: asrText,
      translation: translatedText || asrText,
    },
  };
}

async function fetchBaaniTranslation(audioBlob, normalizedLang) {
  log("Calling Baani ASR - lang:", normalizedLang);
  const formData = new FormData();
  formData.append("file", audioBlob, "recording.webm");
  formData.append("lang", normalizedLang);
  const isEng = normalizedLang === "en" || normalizedLang === "en-IN";
  return isEng ? voiceTranslate(formData, true) : OtherLanguageAsr(formData);
}

async function processAudioTranslation(audioBlob, langConfig, model) {
  const normalizedLang = langConfig.code;
  let translationData = null;

  if (model === "sarvam") {
    translationData = await fetchSarvamTranslation(audioBlob, normalizedLang);
  } else if (model === "baani") {
    translationData = await fetchBaaniTranslation(audioBlob, normalizedLang);
  } else {
    const formData = new FormData();
    formData.append("file", audioBlob, "recording.webm");
    formData.append("lang", normalizedLang);
    translationData = await callTranslationWithRetry(formData, langConfig.backend);
  }

  if (!translationData || translationData.error)
    return { error: translationData?.error || "Invalid translation response" };

  const fallbackText = extractTranscriptText(translationData);
  let displayText = fallbackText;
  let backendText = fallbackText;

  if (translationData?.translation) {
    const translation = translationData.translation;
    if (typeof translation === "object") {
      displayText =
        translation.asr ||
        translation.transcript ||
        translation.text ||
        translation.translation ||
        translation.translated_text ||
        fallbackText;
      backendText =
        translation.translation ||
        translation.translated_text ||
        translation.text ||
        translation.transcript ||
        translation.asr ||
        fallbackText;
    } else if (typeof translation === "string") {
      displayText = translation;
      backendText = translation;
    }
  }

  return { displayText, backendText };
}

const LANG_MAP = {
  "en-IN": { code: "en", backend: "en", locale: "en-IN", name: "English" },
  "te-IN": { code: "te", backend: "te", locale: "te-IN", name: "తెలుగు" },
  "hi-IN": { code: "hi", backend: "hi", locale: "hi-IN", name: "हिन्दी" },
  "or-IN": { code: "or", backend: "or", locale: "or-IN", name: "ଓଡ଼ିଆ" },
};
const SARVAM_LANG_MAP = {
  "en-IN": { code: "en-IN", backend: "en", locale: "en-IN", name: "English" },
  "te-IN": { code: "te-IN", backend: "te", locale: "te-IN", name: "తెలుగు" },
  "hi-IN": { code: "hi-IN", backend: "hi", locale: "hi-IN", name: "हिन्दી" },
  "od-IN": { code: "od-IN", backend: "or", locale: "od-IN", name: "ଓଡ଼ିଆ" },
};
const MODEL_MAP = {
  sarvam: { code: "sarvam", name: "Sarvam" },
  baani: { code: "baani", name: "BAANI" },
};

const MOBILE_NUMBER = {
  "8179445643": "",
  "6305251679": "",
  "9676650473": "",
  "7008279837": "matiapada",
  "7008385951": "Jhirpani",
  "8260522809": "Karmabahal",
  "8658925693": "Chuabahala"
};
function extractBotResponse(data) {
  const responseText = data?.answer || "Please Repeat Your Response.";
  const audioBase64 = data?.audio_base64 || null;
  const originalText = (data?.original_answer || data?.answer || "").toLowerCase();
  const intent = data?.intent || "odisha_scheme";
  const sessionEnded = data?.session_ended === true;

  return {
    responseText,
    audioBase64,
    originalText,
    intent,
    sessionEnded,
  };
}

function checkIsEnding(originalText) {
  if (!originalText) return false;
  const lowerText = originalText.toLowerCase();

  if (lowerText.includes("thank you for using the odisha schemes assistant. have a great day! feel free to return anytime you have questions about government schemes.")) {
    return true;
  }

  for (const text of Object.values(ENDING_TEXTS)) {
    if (lowerText.includes(text.toLowerCase())) {
      return true;
    }
  }

  return false;
}
function checkIsShortReply(asrText) {
  const cleaned = (asrText || "").trim().replace(/[.,!?।]/g, "");
  const normalized = cleaned.toLowerCase();
  const words = normalized.split(/\s+/).filter(Boolean);
  const uniqueWords = [...new Set(words)];
  const collapsed = uniqueWords.join(" ");

  const replyMap = {
    yes: "yes", yup: "yes", no: "no", nope: "no",
    అవును: "yes", అవను: "yes", కాదు: "no", లేదు: "no", సరే: "yes",
    "హాँ": "yes", "नहीं": "no", "ठीक": "yes",
    "ହଁ": "yes", "ନା": "no", "ଠିକ": "yes",
  };

  let mapped = replyMap[cleaned] || replyMap[collapsed] || replyMap[normalized] || collapsed;
  const yesV = ["yes", "yeah", "yep", "yup", "ok", "okay", "correct", "right", "sure", "fine", "yas", "yess"];
  const noV = ["no", "nope", "nah", "noo", "nop"];
  if (yesV.includes(mapped)) mapped = "yes";
  else if (noV.includes(mapped)) mapped = "no";

  if (uniqueWords.length > 1 && !yesV.includes(mapped) && !noV.includes(mapped)) return false;
  return ["yes", "no"].includes(mapped.trim());
}

const WS_MAX_RETRIES = 5;
const WS_BASE_DELAY_MS = 1000;

function buildWsUrl() {
  if (API_BASE_URL && API_BASE_URL.startsWith("http")) {
    const parsed = new URL(API_BASE_URL);
    const proto = parsed.protocol === "https:" ? "wss" : "ws";
    return `${proto}://${parsed.host}/ws/audio`;
  }
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  const devHost = "localhost:8000";
  const host = window.location.hostname === "localhost" ? devHost : window.location.host;
  return `${proto}://${host}/ws/audio`;
}


const GREETING_RESPONSES = [
  "Welcome back! How can I assist you with an Odisha Government scheme today?",
  "Hi again! Please let me know which Odisha Government scheme you'd like information about.",
  "I'm here to help. Feel free to ask about any Odisha Government scheme, eligibility, benefits, financial assistance, or the application process.",
  "How may I assist you today? Ask me anything about Odisha Government schemes.",
  "Please tell me which Odisha Government scheme you'd like to know more about.",
  "What scheme information are you looking for today? I'm here to help.",
  "Feel free to ask me about any Odisha Government scheme or government benefit.",
  "I'm ready to assist you. What would you like to know about Odisha Government schemes?",
  "Please go ahead with your question about any Odisha Government scheme.",
  "How can I help you today? You can ask about scheme eligibility, benefits, subsidies, or application procedures."
];

const SchemePageInner = ({ autoStart = false, onBack, onAdmin }) => {
  const [transcript, setTranscript] = useState("");
  const [language, setLanguage] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [, setVadStatus] = useState("idle");
  const [model, setModel] = useState("");
  const [showSpeakAgain, setShowSpeakAgain] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [replayingIndex, setReplayingIndex] = useState(null);
  const [selectedMobileNumber, setSelectedMobileNumber] = useState("");
  const [inputText, setInputText] = useState("");
  const [inputMode, setInputMode] = useState("voice");
  const [isSessionStarted, setIsSessionStarted] = useState(false);
  const [isSessionCompleted, setIsSessionCompleted] = useState(false);

  const [isPaused, setIsPaused] = useState(false);
  const [pausedIndex, setPausedIndex] = useState(null);
  const timerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const currentStreamRef = useRef(null);
  const recordingProcessedRef = useRef(false);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const vadDetectorRef = useRef(null);
  const sessionIdRef = useRef(null);
  const passiveListeningRef = useRef(false);
  const inactivityTimerRef = useRef(null);
  const isDoneRef = useRef(false);
  const chatHistoryRef = useRef([]);
  const wsRef = useRef(null);
  const wsReadyRef = useRef(false);
  const wsReconnectAttemptsRef = useRef(0);
  const wsReconnectTimerRef = useRef(null);
  const preBufferRef = useRef([]);
  const preBufferSentRef = useRef(false);
  const isSendingAudioRef = useRef(false);
  const chatContainerRef = useRef(null);
  const playbackPromiseRef = useRef(Promise.resolve());
  const inputModeRef = useRef("voice");
  const isRecorderReadyRef = useRef(false);
  const isStoppingRef = useRef(false);
  const isProcessingRef = useRef(false);
  const isAudioPlayingRef = useRef(false);
  const playingCountRef = useRef(0);
  const recursionDepthRef = useRef(0);
  const appStateRef = useRef("idle");
  const isMountedRef = useRef(true);
  const replayUrlRef = useRef(null);
  const wsAudioRef = useRef(null);
  const currentAudioRef = useRef(null);
  const currentReplayAudioRef = useRef(null);
  const hasShownWelcomeRef = useRef(false);
  const greetingIndexRef = useRef(0);

  // Background music refs
  const bgMusicRef = useRef(null);
  const bgMusicVolumeRef = useRef(0.3);
  const hasStartedBgMusicRef = useRef(false);
  const fadeIntervalRef = useRef(null);

  const isMissingSelection = !model || !language || !selectedMobileNumber;
  const isDisabledMic = isMissingSelection || isAudioPlaying || isProcessing || isDone;
  const isStartSessionDisabled = isProcessing || isMissingSelection || isDone;
  const isTextSubmitDisabled = isProcessing || isAudioPlaying || !inputText.trim() || isMissingSelection || isDone;

  const ACTIVE_LANG_MAP = useMemo(() => (model === "sarvam" ? SARVAM_LANG_MAP : LANG_MAP), [model]);

  const updateChat = useCallback((entry) => {
    setChatHistory((prev) => {
      const next = [...prev, entry];
      chatHistoryRef.current = next;
      return next;
    });
  }, []);

  // ========== BACKGROUND MUSIC MANAGEMENT (IVR STYLE) ==========
  const initializeBackgroundMusic = useCallback(async () => {
    try {
      if (!bgMusicRef.current) {
        const audio = new Audio();

        // Get the relative path from processingMusic import
        let relativePath = processingMusic;

        console.log("[IVR] Initializing background music");

        // Validate processingMusic
        if (!relativePath || typeof relativePath !== "string") {
          console.warn("processingMusic import invalid, trying fallback path");
          relativePath = "/static/media/processing_music.mp3";
        }

        // Convert relative path to absolute URL
        let absoluteUrl = relativePath;
        if (relativePath.startsWith("/")) {
          absoluteUrl = `${window.location.origin}${relativePath}`;
        }

        // Set source on audio element
        audio.src = absoluteUrl;
        audio.loop = true;
        audio.volume = 0;
        audio.preload = "none";
        audio.crossOrigin = "anonymous";
        audio._intentedSrc = absoluteUrl;
        audio._relativePath = relativePath;

        // Event handlers
        audio.addEventListener("error", (e) => {
          const errorCode = audio.error?.code;
          const errorName = ["MEDIA_ERR_ABORTED", "MEDIA_ERR_NETWORK", "MEDIA_ERR_DECODE", "MEDIA_ERR_SRC_NOT_SUPPORTED"][errorCode - 1] || "UNKNOWN";
          console.error("[IVR] Background music error:", { errorCode, errorName, src: audio.src });

          if (audio.networkState === 3 && audio._intentedSrc) {
            setTimeout(() => {
              console.log("[IVR] Attempting recovery");
              audio.src = audio._intentedSrc;
              audio.load();
            }, 1000);
          }
        });

        bgMusicRef.current = audio;
      }
    } catch (e) {
      console.error("[IVR] Failed to initialize background music:", e);
    }
  }, []);

  const startBackgroundMusic = useCallback(async () => {
    if (!bgMusicRef.current) {
      await initializeBackgroundMusic();
    }
    if (!bgMusicRef.current) return;

    // Always try to play if paused, regardless of hasStartedBgMusicRef
    if (bgMusicRef.current.paused) {
      try {
        bgMusicRef.current.volume = 0;
        await bgMusicRef.current.play();
        hasStartedBgMusicRef.current = true;
        console.log("[IVR] Background music playback started/resumed");
      } catch (e) {
        console.warn("[IVR] Background music play failed:", e?.message || e);
      }
    }
  }, [initializeBackgroundMusic]);

  // IVR-style volume control - no pause/play, just volume management
  const setMusicVolume = useCallback((targetVolume) => {
    if (!bgMusicRef.current) return;

    const current = bgMusicRef.current.volume;
    if (Math.abs(current - targetVolume) < 0.01) {
      bgMusicRef.current.volume = targetVolume;
      return;
    }

    // Clear any existing fade
    if (fadeIntervalRef.current) {
      clearInterval(fadeIntervalRef.current);
      fadeIntervalRef.current = null;
    }

    // Smooth fade
    let volume = current;
    const step = (targetVolume - current) / 10;
    let count = 0;

    fadeIntervalRef.current = setInterval(() => {
      count++;
      volume += step;

      if ((step > 0 && volume >= targetVolume) || (step < 0 && volume <= targetVolume)) {
        bgMusicRef.current.volume = targetVolume;
        clearInterval(fadeIntervalRef.current);
        fadeIntervalRef.current = null;
      } else {
        bgMusicRef.current.volume = volume;
      }
    }, 30);
  }, []);

  // Helper: Set music for processing state (hold music at medium volume)
  const setProcessingMusicVolume = useCallback(() => {
    setMusicVolume(0.45);
  }, [setMusicVolume]);

  // Helper: Set music for bot speaking (ducked low under bot voice)
  const setBotSpeakingMusicVolume = useCallback(() => {
    setMusicVolume(0.35);
  }, [setMusicVolume]);

  // Helper: Silence music (user input or waiting)
  const silenceMusicVolume = useCallback(() => {
    setMusicVolume(0);
  }, [setMusicVolume]);
  // ========== END BACKGROUND MUSIC MANAGEMENT ==========

  const resetToIdle = () => {
    setIsProcessing(false);
    isProcessingRef.current = false;
    setIsRecording(false);
    isSendingAudioRef.current = false;
    isStoppingRef.current = false;
    audioChunksRef.current = [];
    recursionDepthRef.current = 0;
    appStateRef.current = "idle";
  };

  const connectWebSocket = () => {
    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }
    if (wsReconnectAttemptsRef.current >= WS_MAX_RETRIES) {
      warn("WS: max reconnect attempts reached — giving up");
      return;
    }

    wsReadyRef.current = false;
    const wsUrl = buildWsUrl();
    log(`WS: connecting (attempt ${wsReconnectAttemptsRef.current + 1}/${WS_MAX_RETRIES}) → ${wsUrl}`);

    let ws;
    try {
      ws = new WebSocket(wsUrl);
    } catch (e) {
      err("WS: construction failed:", e);
      scheduleWsReconnect();
      return;
    }

    ws.onopen = () => {
      log("WS: connected ✓");
      wsReadyRef.current = true;
      wsReconnectAttemptsRef.current = 0;
      if (wsReconnectTimerRef.current) {
        clearTimeout(wsReconnectTimerRef.current);
        wsReconnectTimerRef.current = null;
      }
      try {
        ws.send(
          JSON.stringify({
            type: "init",
            session_id: sessionIdRef.current,
            lang: language || "en-IN",
          })
        );
      } catch (e) {
        warn("WS: init send failed", e);
      }
    };

    ws.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data);
        if (data.type === "text" && typeof data.text === "string" && data.text.trim()) {
          setTranscript(data.text);
        }
        if (data.type === "audio" && data.audio) {
          playBase64Audio(data.audio);
        }
      } catch (e) {
        warn("WS: message parse failed", e);
      }
    };

    ws.onerror = (e) => { err("WS: error", e); };

    ws.onclose = (evt) => {
      log("WS: closed", evt.code, evt.reason);
      wsReadyRef.current = false;
      wsRef.current = null;

      if (!isDoneRef.current && inputModeRef.current === "voice") {
        scheduleWsReconnect();
      }
    };

    wsRef.current = ws;
  };

  const scheduleWsReconnect = () => {
    if (wsReconnectAttemptsRef.current >= WS_MAX_RETRIES) return;
    if (wsReconnectTimerRef.current) return;

    wsReconnectAttemptsRef.current += 1;
    const delay = WS_BASE_DELAY_MS * Math.pow(2, wsReconnectAttemptsRef.current - 1);
    log(`WS: scheduling reconnect #${wsReconnectAttemptsRef.current} in ${delay}ms`);

    wsReconnectTimerRef.current = setTimeout(() => {
      wsReconnectTimerRef.current = null;
      if (!isDoneRef.current && inputModeRef.current === "voice") {
        connectWebSocket();
      }
    }, delay);
  };

  const sendAudioChunk = async (blob) => {
    if (!blob || blob.size === 0) return;
    try {
      if (appStateRef.current !== "recording") return;
      if (
        wsRef.current?.readyState === WebSocket.OPEN &&
        wsReadyRef.current &&
        isSendingAudioRef.current
      ) {
        wsRef.current.send(await blob.arrayBuffer());
      }
    } catch (e) {
      warn("sendAudioChunk: failed", e);
    }
  };

  const playBase64Audio = async (base64ArrayOrSingle) => {
    playbackPromiseRef.current = playbackPromiseRef.current.then(async () => {
      const dataList = Array.isArray(base64ArrayOrSingle) ? base64ArrayOrSingle : [base64ArrayOrSingle];

      if (!dataList.length || !dataList.some(d => d)) {
        console.warn("playBase64Audio: no valid audio data provided");
        return;
      }

      setIsAudioPlaying(true);

      // Bot speaking: lower background music (IVR style - don't mute)
      setBotSpeakingMusicVolume();

      // STOP VAD - don't accept user input while bot speaking
      if (vadDetectorRef.current) {
        try { vadDetectorRef.current.stop(); } catch (_) { }
      }

      try {
        for (const base64Data of dataList) {
          if (!base64Data) continue;

          // Validate base64 string
          if (typeof base64Data !== "string" || base64Data.length < 100) {
            console.warn("playBase64Audio: invalid base64 data (too short or not string)", base64Data?.length);
            continue;
          }

          // Quick base64 format validation
          if (!/^[A-Za-z0-9+/=]*$/.test(base64Data)) {
            console.warn("playBase64Audio: invalid base64 format (contains invalid characters)");
            continue;
          }

          await new Promise((resolve, reject) => {
            try {
              const byteCharacters = atob(base64Data);
              const byteNumbers = new Uint8Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }

              let mimeType = "audio/wav";
              // Decode first 16 bytes to detect format from header
              const headerBytes = base64Data.substring(0, 24); // ~16 bytes in base64
              try {
                const headerStr = atob(headerBytes);
                // Check for MP3: 0xFF 0xFB or 0xFF 0xFA or ID3 tag

                if (headerStr.charCodeAt(0) === 0xFF && (headerStr.charCodeAt(1) & 0xE0) === 0xE0) {
                  mimeType = "audio/mpeg";
                }
                // Check for RIFF header (WAV/AVI)
                else if (headerStr.charCodeAt(0) === 0x52 && headerStr.charCodeAt(1) === 0x49 &&
                  headerStr.charCodeAt(2) === 0x46 && headerStr.charCodeAt(3) === 0x46) {
                  mimeType = "audio/wav";
                }
                // Check for EBML header (WebM/Matroska)
                else if (headerStr.charCodeAt(0) === 0x1A && headerStr.charCodeAt(1) === 0x45) {
                  mimeType = "audio/webm";
                }
                // Check for Ogg
                else if (headerStr.charCodeAt(0) === 0x4F && headerStr.charCodeAt(1) === 0x67 &&
                  headerStr.charCodeAt(2) === 0x67 && headerStr.charCodeAt(3) === 0x53) {
                  mimeType = "audio/ogg";
                }
                // Check for MP4/M4A (ftyp box)
                else if (headerStr.charCodeAt(4) === 0x66 && headerStr.charCodeAt(5) === 0x74 &&
                  headerStr.charCodeAt(6) === 0x79 && headerStr.charCodeAt(7) === 0x70) {
                  mimeType = "audio/mp4";
                }
              } catch (e) {
                console.warn("Could not detect audio format from header, defaulting to wav", e);
              }

              const audioBlob = new Blob([byteNumbers], { type: mimeType });
              const audioUrl = URL.createObjectURL(audioBlob);
              const audio = new Audio(audioUrl);
              audio.preload = "auto";
              audio.volume = 1.0;
              audio.crossOrigin = "anonymous";
              currentAudioRef.current = audio;

              let audioEnded = false;
              const cleanup = () => {
                if (currentAudioRef.current === audio) currentAudioRef.current = null;
                try { URL.revokeObjectURL(audioUrl); } catch (e) { }
              };

              audio.addEventListener("ended", () => {
                audioEnded = true;
                cleanup();
                resolve();
              });

              audio.addEventListener("error", (e) => {
                console.error("playBase64Audio track error:", e);
                cleanup();
                resolve();
              });

              const timeoutMs = 45000;
              const timeoutId = setTimeout(() => {
                if (!audioEnded) {
                  console.warn("playBase64Audio track timeout");
                  audioEnded = true;
                  cleanup();
                  resolve();
                }
              }, timeoutMs);

              const startPlayback = async () => {
                if (isPaused) {
                  // Wait for resume
                  const check = () => {
                    if (!isPaused) {
                      audio.play().catch((e) => {
                        console.error("playBase64Audio play() error:", e);
                        clearTimeout(timeoutId);
                        cleanup();
                        resolve();
                      });
                    } else {
                      setTimeout(check, 100);
                    }
                  };
                  check();
                } else {
                  audio.play().catch((e) => {
                    console.error("playBase64Audio play() error:", e);
                    clearTimeout(timeoutId);
                    cleanup();
                    resolve();
                  });
                }
              };

              startPlayback();
            } catch (error) {
              console.error("playBase64Audio inner play error:", error);
              resolve();
            }
          });
        }
      } catch (error) {
        console.error("playBase64Audio sequence error:", error);
      } finally {
        setIsAudioPlaying(false);
        isAudioPlayingRef.current = false;
      }
    }).catch(err => {
      console.error("Global playback chain error:", err);
      return Promise.resolve();
    });

    return playbackPromiseRef.current;
  };

  const setupAudioContext = (stream) => {
    if (!audioCtxRef.current)
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    if (!analyserRef.current) {
      analyserRef.current = audioCtxRef.current.createAnalyser();
      analyserRef.current.fftSize = 2048;
    }
    if (!sourceRef.current) {
      sourceRef.current = audioCtxRef.current.createMediaStreamSource(stream);
      sourceRef.current.connect(analyserRef.current);
    }
  };

  const setupMediaRecorder = (stream) => {
    try {
      let mimeType = "audio/webm;codecs=opus";
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        if (MediaRecorder.isTypeSupported("audio/mp4")) {
          mimeType = "audio/mp4";
        } else if (MediaRecorder.isTypeSupported("audio/ogg")) {
          mimeType = "audio/ogg";
        }
      }
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      recordingProcessedRef.current = false;
      audioChunksRef.current = [];
      mediaRecorderRef.current.start();

      const streamInterval = setInterval(() => {
        if (mediaRecorderRef.current?.state === "recording") {
          try { mediaRecorderRef.current.requestData(); } catch (e) { warn("requestData error", e); }
        }
      }, 120);

      mediaRecorderRef.current.onstart = () => {
        log("mediaRecorder: onstart");
        isRecorderReadyRef.current = true;
        isStoppingRef.current = false;
      };

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (!e.data || e.data.size === 0) return;
        audioChunksRef.current.push(e.data);

        if (isSendingAudioRef.current) {
          sendAudioChunk(e.data).catch((ex) => warn("sendAudioChunk error", ex));
        }

        if (!preBufferSentRef.current) {
          preBufferRef.current.push(e.data);
          if (preBufferRef.current.length > 5) preBufferRef.current.shift();
        }

        if (audioChunksRef.current.length > 300) {
          audioChunksRef.current = audioChunksRef.current.slice(-200);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        clearInterval(streamInterval);
        log("mediaRecorder: onstop");
        if (recordingProcessedRef.current) return;

        isSendingAudioRef.current = false;
        preBufferSentRef.current = false;
        isRecorderReadyRef.current = false;
        isStoppingRef.current = false;
        recordingProcessedRef.current = true;
        clearInterval(timerRef.current);

        if (!audioChunksRef.current.length) { setTranscript(""); return; }

        await new Promise((r) => setTimeout(r, 200));
        const audioBlob = new Blob(audioChunksRef.current, {
          type: mediaRecorderRef.current.mimeType,
        });
        log("audioBlob created — size:", audioBlob.size);

        audioChunksRef.current = [];

        await processRecordingOnStop(audioBlob);
      };
    } catch (e) {
      err("setupMediaRecorder error", e);
      mediaRecorderRef.current = null;
    }
  };

  const flushPreBuffer = async () => {
    if (!preBufferRef.current.length || preBufferSentRef.current) return;
    preBufferSentRef.current = true;
    if (wsRef.current?.readyState === WebSocket.OPEN && wsReadyRef.current) {
      for (const chunk of preBufferRef.current) {
        try { wsRef.current.send(await chunk.arrayBuffer()); } catch (e) { warn("flushPreBuffer send failed", e); }
      }
    }
  };

  const startPassiveListening = async () => {
    log("startPassiveListening: called");
    if (inputModeRef.current !== "voice") return;

    if (passiveListeningRef.current) {
      if (vadDetectorRef.current) try { vadDetectorRef.current.start(); } catch (_) { }
      return;
    }
    passiveListeningRef.current = true;

    try {
      if (!navigator.mediaDevices?.getUserMedia)
        throw new Error("Microphone access requires HTTPS or a secure context.");

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      log("startPassiveListening: stream acquired");
      currentStreamRef.current = stream;

      connectWebSocket();
      setupMediaRecorder(stream);
      setupAudioContext(stream);

      if (vadDetectorRef.current) {
        try { vadDetectorRef.current.destroy(); } catch (_) { }
        vadDetectorRef.current = null;
      }

      const vadDetector = new VADDetector({
        threshold: 0.06,
        silenceTimeout: 500,
        smoothing: 0.7,
        useExternalAnalyser: analyserRef.current,

        onSpeechStart: () => {
          try {
            if (appStateRef.current === "processing" || appStateRef.current === "playing" || isStoppingRef.current) return;
            const rec = mediaRecorderRef.current;
            if (!rec) return;
            if (rec.state === "inactive") {
              log("VAD: restarting inactive recorder");
              recordingProcessedRef.current = false;
              if (!currentStreamRef.current) { warn("No stream for restart"); return; }
              setupMediaRecorder(currentStreamRef.current);
            }
            if (inactivityTimerRef.current) { clearTimeout(inactivityTimerRef.current); inactivityTimerRef.current = null; }
            isSendingAudioRef.current = true;
            if (!preBufferSentRef.current && preBufferRef.current.length > 0) flushPreBuffer();
            setTranscript("Recording...");
            appStateRef.current = "recording";
            // User speaking: silence the background music
            silenceMusicVolume();
          } catch (e) { err("onSpeechStart error:", e); }
        },

        onSpeechEnd: () => {
          try {
            if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
            inactivityTimerRef.current = setTimeout(() => {
              try {
                isSendingAudioRef.current = false;
                if (!mediaRecorderRef.current || mediaRecorderRef.current.state !== "recording") return;
                if (audioChunksRef.current.length < 5) { log("VAD: too short — ignoring"); return; }
                isStoppingRef.current = true;
                mediaRecorderRef.current.stop();
              } catch (e) { err("VAD onSpeechEnd stop failed:", e); }
            }, 1800);
          } catch (e) { err("onSpeechEnd error:", e); }
        },
      });

      const initialized = await vadDetector.init(stream, analyserRef.current);
      if (initialized) {
        vadDetectorRef.current = vadDetector;
        vadDetectorRef.current.start();
        setVadStatus("listening");
        setIsRecording(true);
      }
    } catch (e) {
      err("startPassiveListening failed:", e);
      setTranscript(e.message || "Microphone access failed — check permissions.");
      passiveListeningRef.current = false;
    }
  };

  const stopListeningCompletely = () => {
    log("stopListeningCompletely");
    passiveListeningRef.current = false;

    if (inactivityTimerRef.current) { clearTimeout(inactivityTimerRef.current); inactivityTimerRef.current = null; }
    if (wsReconnectTimerRef.current) { clearTimeout(wsReconnectTimerRef.current); wsReconnectTimerRef.current = null; }
    wsReconnectAttemptsRef.current = WS_MAX_RETRIES;

    if (vadDetectorRef.current) { try { vadDetectorRef.current.destroy(); } catch (e) { /* ignore */ } vadDetectorRef.current = null; }
    try { if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop(); } catch (e) { /* ignore */ }

    if (currentStreamRef.current) {
      try { currentStreamRef.current.getTracks().forEach((t) => t.stop()); } catch (e) { /* ignore */ }
      currentStreamRef.current = null;
    }
    if (wsRef.current) { try { wsRef.current.close(); } catch (e) { /* ignore */ } wsRef.current = null; }
    try {
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => { });
        audioCtxRef.current = null;
      }
    } catch (e) { /* ignore */ }

    setIsRecording(false);
    isSendingAudioRef.current = false;
    preBufferSentRef.current = false;
    appStateRef.current = "idle";
    setVadStatus("done");
    // Silence background music when listening stops
    silenceMusicVolume();
  };

  const speakSystemMessage = async (messageKey = "speakAgain") => {
    try {
      const activeLang = language || "en-IN";
      const langCode = ACTIVE_LANG_MAP[activeLang]?.code || "en-IN";
      const text = SPEAK_AGAIN_MAP[activeLang] || SPEAK_AGAIN_MAP["en-IN"] || SPEAK_AGAIN_MAP["en"];

      log("speakSystemMessage:", messageKey, text, "lang:", langCode, "model:", model);

      let res = null;
      if (model === "sarvam") {
        res = await ttsSarvam(text, langCode);
      } else {
        res = await ttsBani({ text, lang: langCode });
      }

      const audioData = res?.audio_base64 || res?.sarvam_audio_base64 || res?.bhashini_audio_base64 || res?.audio;
      if (audioData) {
        await playBase64Audio(audioData);
      }
    } catch (e) {
      err("speakSystemMessage failed:", e);
    }
  };

  const getWelcomeText = (languageKey) => {
    const prefix = String(languageKey || "").split("-")[0];
    return (
      WELCOME_MESSAGE_MAP[languageKey] ||
      WELCOME_MESSAGE_MAP[`${prefix}-IN`] ||
      WELCOME_MESSAGE_MAP["en-IN"] ||
      WELCOME_MESSAGE_MAP["en"] ||
      "Hello, I am the Scheme Agent. How can I help you?"
    );
  };

  const speakText = async (text) => {
    if (!text) return;
    try {
      const activeLang = language || "en-IN";
      const langCode = ACTIVE_LANG_MAP[activeLang]?.code || "en-IN";
      log("speakText:", text, "lang:", langCode, "model:", model);

      let res = null;
      if (model === "sarvam") {
        res = await ttsSarvam(text, langCode);
      } else {
        res = await ttsBani({ text, lang: langCode });
      }

      const audioData = res?.audio_base64 || res?.sarvam_audio_base64 || res?.bhashini_audio_base64 || res?.audio;
      if (audioData) {
        await playBase64Audio(audioData);
      }
    } catch (e) {
      err("speakText failed:", e);
    }
  };

  const processRecordingOnStop = async (audioBlob) => {
    const isValid = await validateWebMBlob(audioBlob);
    if (!isValid) {
      warn("Invalid WebM — skipping");
      setShowSpeakAgain(true);
      setTimeout(() => setShowSpeakAgain(false), 3000);
      resetToIdle();
      return;
    }
    if (!passiveListeningRef.current) return;

    try {
      setIsRecording(false);
      setIsProcessing(true);
      isProcessingRef.current = true;
      appStateRef.current = "processing";
      setTranscript("Translating...");
      if (vadDetectorRef.current) {
        try { vadDetectorRef.current.stop(); } catch (_) { }
      }
      await startBackgroundMusic();
      setProcessingMusicVolume();

      const langConfig = ACTIVE_LANG_MAP[language] || ACTIVE_LANG_MAP["en-IN"];
      const res = await processAudioTranslation(audioBlob, langConfig, model);

      if (res.error) {
        if (res.error === "Empty ASR") {
          setShowSpeakAgain(true);
          setTimeout(() => setShowSpeakAgain(false), 5000);
          setIsProcessing(false);
          isProcessingRef.current = false;
          appStateRef.current = "idle";
          const msg = SPEAK_AGAIN_MAP[language] || SPEAK_AGAIN_MAP["en-IN"];
          setTranscript(msg);
          await speakSystemMessage();

          setTimeout(async () => {
            if (!isDoneRef.current && inputModeRef.current === "voice") {
              wsReconnectAttemptsRef.current = 0;
              silenceMusicVolume(); // Silence music before resuming listening
              if (vadDetectorRef.current) {
                try { vadDetectorRef.current.start(); } catch (_) { }
              }
              await startPassiveListening();
              setTranscript("Waiting for speech...");
            }
          }, 300);
          return;
        }
        setIsProcessing(false);
        isProcessingRef.current = false;
        appStateRef.current = "idle";
        setTranscript("Backend error: " + res.error);
        return;
      }

      const { displayText, backendText } = res;
      const cleanedDisplayText = sanitizeText(displayText);
      const cleanedBackendText = sanitizeText(backendText || cleanedDisplayText);

      if (!cleanedDisplayText?.trim()) {
        setIsProcessing(false);
        isProcessingRef.current = false;
        appStateRef.current = "idle";
        const msg = SPEAK_AGAIN_MAP[language] || SPEAK_AGAIN_MAP["en-IN"];
        setTranscript(msg);
        await speakSystemMessage();

        setTimeout(async () => {
          if (!isDoneRef.current && inputModeRef.current === "voice") {
            wsReconnectAttemptsRef.current = 0;
            silenceMusicVolume(); // Silence music before resuming listening
            if (vadDetectorRef.current) {
              try { vadDetectorRef.current.start(); } catch (_) { }
            }
            await startPassiveListening();
            setTranscript("Waiting for speech...");
          }
        }, 300);
        return;
      }

      handleShortReplyDetection(cleanedDisplayText);

      setTranscript(cleanedDisplayText);

      const userEntry = { from: "user", text: cleanedDisplayText, audio: audioBlob };
      updateChat(userEntry);

      const langCfg = ACTIVE_LANG_MAP[language] || ACTIVE_LANG_MAP["en-IN"];
      const bookingData = await callSchemeAgent(
        cleanedBackendText,
        sessionIdRef.current,
        langCfg?.code,
        model
      );

      await handleBotResponse(bookingData, langCfg);
    } catch (e) {
      err("processRecordingOnStop failed:", e);
      resetToIdle();
      setTranscript("Error processing — please try again.");
    } finally {
      resetToIdle();
    }
  };



  const handleBotResponse = async (data, langConfig, type = "normal") => {
    log(`handleBotResponse [${type}]:`, data);
    if (!data) { resetToIdle(); setTranscript("An error occurred — please try again."); return; }

    const localizedData = await translateSchemeAgentResponse(data, langConfig?.code || "en");
    const {
      responseText,
      audioBase64,
      originalText,
      intent,
      sessionEnded,
    } = extractBotResponse(localizedData);

    let botText = responseText;

    console.log("intent:", intent);
    console.log("hasShownWelcome:", hasShownWelcomeRef.current);
    console.log("responseText:", responseText);

    if (intent === "greeting" && hasShownWelcomeRef.current) {
      console.log("Changing greeting...");
      botText = GREETING_RESPONSES[greetingIndexRef.current];
      greetingIndexRef.current = (greetingIndexRef.current + 1) % GREETING_RESPONSES.length;
    }

    // Start music on first response (if not already started)
    await startBackgroundMusic();

    if (botText) {
      if (inputModeRef.current === "voice") {
        let ttsAudio = audioBase64;
        try {
          if (!ttsAudio) {
            let ttsRes = null;

            const activeLang = language || "en-IN";
            const langCode =
              ACTIVE_LANG_MAP[activeLang]?.code ||
              langConfig?.code ||
              "en-IN";

            if (model === "sarvam") {
              ttsRes = await ttsSarvam(botText, langCode);
            } else {
              ttsRes = await ttsBani({
                text: botText,
                lang: langCode,
              });
            }

            ttsAudio =
              ttsRes?.audio_base_64 ||
              ttsRes?.sarvam_audio_base_64 ||
              ttsRes?.bhashini_audio_base_64 ||
              ttsRes?.audio;
          }
        } catch (e) {
          err("TTS synthesis failed:", e);
        }

        // Show the bot response in the UI and play the audio together
        const entry = { from: "bot", text: botText };
        updateChat(entry);
        setTranscript(botText);

        if (ttsAudio) {
          setIsAudioPlaying(true);
          setBotSpeakingMusicVolume();
          appStateRef.current = "playing";
          try {
            await playBase64Audio(ttsAudio);
          } catch (e) {
            err("Response audio playback failed:", e);
          } finally {
            setIsAudioPlaying(false);
            appStateRef.current = "idle";
          }
        }
      } else {
        // Text mode: display immediately
        const entry = { from: "bot", text: botText };
        updateChat(entry);
        setTranscript(botText);
      }
    }

    if (
      sessionEnded ||
      intent === "SESSION_ENDED" ||
      intent === "ending" ||
      checkIsEnding(originalText) ||
      (!originalText && !responseText)
    ) {
      stopListeningCompletely();
      sessionIdRef.current = null;
      setIsDone(true);
      isDoneRef.current = true;
      setIsSessionStarted(false);
      setIsSessionCompleted(true);
    }

    resetToIdle();

    if (!isDoneRef.current && inputModeRef.current === "voice" && type !== "welcome") {
      wsReconnectAttemptsRef.current = 0;
      // Silence music and resume VAD listening
      silenceMusicVolume();
      if (vadDetectorRef.current) {
        try { vadDetectorRef.current.start(); } catch (_) { }
      }
      await startPassiveListening();
      setTranscript("Waiting for speech...");
    }
  };

  const handleStartSession = async () => {
    if (isMissingSelection) {
      setTranscript("Please select Model, Language & Mobile Number first.");
      return;
    }
    try {
      setIsProcessing(true);
      isProcessingRef.current = true;
      appStateRef.current = "processing";
      setIsDone(false);
      isDoneRef.current = false;
      recursionDepthRef.current = 0;

      if (!sessionIdRef.current) {
        sessionIdRef.current = generateUUID();
      }

      const langConfig = ACTIVE_LANG_MAP[language] || ACTIVE_LANG_MAP["en-IN"];
      const welcomeData = await fetchSchemeWelcomeMessage(langConfig.code, sessionIdRef.current, model);
      const displayedText = welcomeData?.text?.trim() || getWelcomeText(langConfig.backend);

      if (displayedText) {
        let audioData = welcomeData?.audioBase64 || "";
        let ttsResult = null;

        if (inputModeRef.current === "voice") {
          try {
            if (!audioData) {
              const activeLang = language || "en-IN";
              const langCode =
                ACTIVE_LANG_MAP[activeLang]?.code ||
                langConfig?.code ||
                "en-IN";

              if (model === "sarvam") {
                ttsResult = await ttsSarvam(displayedText, langCode);
              } else {
                ttsResult = await ttsBani({
                  text: displayedText,
                  lang: langCode,
                });
              }

              audioData =
                ttsResult?.audio_base_64 ||
                ttsResult?.sarvam_audio_base_64 ||
                ttsResult?.bhashini_audio_base_64 ||
                ttsResult?.audio;
            }
          } catch (e) {
            warn("Failed to generate welcome TTS:", e);
          }
        }

        // Show welcome text in UI only after audio is fetched
        updateChat({
          from: "bot",
          text: displayedText,
        });
        setTranscript(displayedText);

        if (audioData) {
          await playBase64Audio(audioData);
        }
        hasShownWelcomeRef.current = true;
        console.log("TTS RESPONSE", ttsResult);
      }
      console.log("WELCOME TEXT:", displayedText);
      console.log("MODEL:", model);
      console.log("VOICE MODE:", inputModeRef.current);

      setIsSessionStarted(true);
      // Start listening for user input instead of sending auto "hai"
      setIsProcessing(false);
      isProcessingRef.current = false;
      appStateRef.current = "idle";
      await startPassiveListening();
    } catch (e) {
      err("handleStartSession failed:", e);
      resetToIdle();
      setTranscript("Failed to start session — please try again.");
      silenceMusicVolume();
    } finally {
      if (!isDoneRef.current) resetToIdle();
    }
  };

  const handleTextSubmit = async (e) => {
    if (e) e.preventDefault();
    if (isMissingSelection) {
      setTranscript("Please select Model, Language & Mobile Number first.");
      return;
    }
    if (!inputText.trim()) return;

    setInputMode("text");
    inputModeRef.current = "text";
    stopListeningCompletely();

    const textToSend = sanitizeText(inputText);
    setInputText("");

    if (!textToSend) return;

    const userEntry = { from: "user", text: textToSend };
    updateChat(userEntry);

    try {
      setIsProcessing(true);
      isProcessingRef.current = true;
      appStateRef.current = "processing";
      setTranscript("Processing...");
      recursionDepthRef.current = 0;

      const langConfig = ACTIVE_LANG_MAP[language] || ACTIVE_LANG_MAP["en-IN"];

      let backendTextToSend = textToSend;
      if (langConfig.code !== "en" && langConfig.code !== "en-IN") {
        try {
          const transRes = await translateSarvam(textToSend, "en", langConfig.code);
          backendTextToSend =
            transRes?.translation ||
            transRes?.translated_text ||
            transRes?.text ||
            transRes?.transcript ||
            textToSend;
        } catch (e) {
          warn("Text translation failed:", e);
        }
      }

      const bookingData = await callSchemeAgent(
        backendTextToSend,
        sessionIdRef.current,
        langConfig?.code,
        model
      );

      await handleBotResponse(bookingData, langConfig);
    } catch (e) {
      err("handleTextSubmit failed:", e);
      resetToIdle();
      setTranscript("Error processing request — please try again.");
      silenceMusicVolume();
    } finally {
      if (!isDoneRef.current) resetToIdle();
    }
  };

  const handleStartRecording = async () => {
    if (inputModeRef.current !== "voice") {
      setTranscript("Voice is blocked while Text Mode is active. Switch to Voice Mode to speak.");
      return;
    }
    if (isMissingSelection) {
      setTranscript("Please select Model, Language & Mobile Number first.");
      return;
    }
    if (appStateRef.current !== "idle") { log("Blocked: system busy"); return; }

    setInputMode("voice");
    inputModeRef.current = "voice";
    setIsDone(false);
    isDoneRef.current = false;
    setIsProcessing(false);
    isProcessingRef.current = false;
    appStateRef.current = "idle";
    recursionDepthRef.current = 0;

    if (isRecording) {
      stopListeningCompletely();
      return;
    }

    if (!sessionIdRef.current) {
      sessionIdRef.current = generateUUID();
    }
    wsReconnectAttemptsRef.current = 0;

    setTranscript("Preparing to listen...");
    setIsRecording(true);
    audioChunksRef.current = [];

    try {
      const langConfig = ACTIVE_LANG_MAP[language] || ACTIVE_LANG_MAP["en-IN"];
      const welcomeData = await fetchSchemeWelcomeMessage(langConfig.code, sessionIdRef.current, model);
      const displayedText = welcomeData?.text?.trim() || getWelcomeText(langConfig.backend);

      if (displayedText) {
        let audioData = welcomeData?.audioBase64 || "";
        let ttsResult = null;

        if (inputModeRef.current === "voice") {
          try {
            if (!audioData) {
              const activeLang = language || "en-IN";
              const langCode =
                ACTIVE_LANG_MAP[activeLang]?.code ||
                langConfig?.code ||
                "en-IN";

              if (model === "sarvam") {
                ttsResult = await ttsSarvam(displayedText, langCode);
              } else {
                ttsResult = await ttsBani({
                  text: displayedText,
                  lang: langCode,
                });
              }

              audioData =
                ttsResult?.audio_base64 ||
                ttsResult?.sarvam_audio_base64 ||
                ttsResult?.bhashini_audio_base64 ||
                ttsResult?.audio;
            }
          } catch (e) {
            warn("Failed to generate welcome TTS:", e);
          }
        }

        // Show welcome text in UI only after audio is fetched
        updateChat({ from: "bot", text: displayedText });
        setTranscript(displayedText);

        if (audioData) {
          await playBase64Audio(audioData);
        }
        hasShownWelcomeRef.current = true;
      }

      if (inputModeRef.current !== "voice") return;
      setTranscript("Listening for your voice...");
      await startPassiveListening();
    } catch (e) {
      err("handleStartRecording failed:", e);
      setTranscript("Please enable microphone permission and try again.");
      resetToIdle();
      silenceMusicVolume();
    }
  };

  const handleShortReplyDetection = (asrText) => {
    const isShort = checkIsShortReply(asrText);
    if (isShort) {
      isSendingAudioRef.current = false;
      try { vadDetectorRef.current?.stop(); } catch (_) { }
      if (mediaRecorderRef.current?.state === "recording") try { mediaRecorderRef.current.stop(); } catch (_) { }
      audioChunksRef.current = [];
      preBufferSentRef.current = false;
    }
    return isShort;
  };

  const handleReplayMessage = async (index) => {
    const message = chatHistory[index];
    if (!message) return;
    try {
      if (
        currentReplayAudioRef.current &&
        replayingIndex === index &&
        !currentReplayAudioRef.current.paused
      ) {
        currentReplayAudioRef.current.pause();
        setPausedIndex(index);
        return;
      }
      if (
        currentReplayAudioRef.current &&
        replayingIndex === index &&
        currentReplayAudioRef.current.paused
      ) {
        await currentReplayAudioRef.current.play();
        setPausedIndex(null);
        return;
      }
      setReplayingIndex(index);
      setPausedIndex(null);
      let audio;
      if (message.from === "user" && message.audio instanceof Blob) {
        const audioUrl = URL.createObjectURL(message.audio);
        replayUrlRef.current = audioUrl;
        audio = new Audio(audioUrl);
        const cleanup = () => {
          revokeAudioUrl(audioUrl);
          if (replayUrlRef.current === audioUrl) {
            replayUrlRef.current = null;
          }
          currentReplayAudioRef.current = null;
          setReplayingIndex(null);
          setPausedIndex(null);
        };
        currentReplayAudioRef.current = audio;
        audio.addEventListener("ended", cleanup);
        audio.addEventListener("error", cleanup);
        await audio.play();
      } else if (message.from === "bot" && message.audio) {
        const audioUrl = createAudioUrlFromBase64(message.audio);
        audio = new Audio(audioUrl);
        const cleanup = () => {
          revokeAudioUrl(audioUrl);
          currentReplayAudioRef.current = null;
          setReplayingIndex(null);
          setPausedIndex(null);
        };
        currentReplayAudioRef.current = audio;
        audio.addEventListener("ended", cleanup);
        audio.addEventListener("error", cleanup);
        await audio.play();
      }
    } catch (e) {
      err("Replay failed:", e);
      currentReplayAudioRef.current = null;
      setReplayingIndex(null);
      setPausedIndex(null);
    }
  };

  const resetSession = () => {
    stopListeningCompletely();
    setIsSessionStarted(false);
    setIsDone(false);
    isDoneRef.current = false;
    setIsSessionCompleted(false);
    setChatHistory([]);
    chatHistoryRef.current = [];
    sessionIdRef.current = null;
    setIsProcessing(false);
    setIsAudioPlaying(false);
    setIsPaused(false);
    setPausedIndex(null);
    currentAudioRef.current = null;
    currentReplayAudioRef.current = null;
    wsReconnectAttemptsRef.current = 0;
  };

  const handleStartRecordingRef = useRef(null);
  useEffect(() => {
    handleStartRecordingRef.current = handleStartRecording;
  });

  useEffect(() => {
    if (!autoStart) return;
    const t = setTimeout(() => {
      try {
        if (inputModeRef.current === "voice") handleStartRecordingRef.current?.();
      } catch (e) { }
    }, 300);
    return () => clearTimeout(t);
  }, [autoStart]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      revokeAudioUrl(replayUrlRef.current);
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (wsReconnectTimerRef.current) clearTimeout(wsReconnectTimerRef.current);
      if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);
      try { vadDetectorRef.current?.destroy(); } catch (_) { }
      try { currentStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) { }
      try {
        if (bgMusicRef.current) {
          bgMusicRef.current.pause();
          bgMusicRef.current.src = "";
        }
      } catch (_) { }
      if (currentAudioRef.current) {
        try {
          currentAudioRef.current.pause();
          currentAudioRef.current.src = "";
        } catch (_) { }
      }
      if (currentReplayAudioRef.current) {
        try {
          currentReplayAudioRef.current.pause();
          currentReplayAudioRef.current.src = "";
        } catch (_) { }
      }
    };
  }, []);

  useEffect(() => {
    if (chatContainerRef.current)
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
  }, [chatHistory]);

  useEffect(() => {
    const handleOutsideClick = () => { };
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, []);

  const togglePlayback = useCallback(() => {
    if (!currentAudioRef.current && !bgMusicRef.current) return;

    if (isPaused) {
      setIsPaused(false);
      if (currentAudioRef.current) {
        currentAudioRef.current.play().catch(e => err("Resume failed:", e));
      }
      // If bot was speaking, keep music ducked
      if (isAudioPlaying) setBotSpeakingMusicVolume();
      else if (isProcessing) setProcessingMusicVolume();
    } else {
      setIsPaused(true);
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
      }
    }
  }, [isPaused, isAudioPlaying, isProcessing, setBotSpeakingMusicVolume, setProcessingMusicVolume]);

  const getStatusText = () => {
    if (showSpeakAgain) return <span style={{ color: "#ff4d4d" }}>Please speak again...</span>;
    if (isSessionCompleted) return "Process completed for this session";
    if (isAudioPlaying) return isPaused ? "Assistant Paused" : "Assistant is Speaking...";
    if (isRecording) return "Waiting for user input...";
    if (isProcessing) return "Processing...";
    return "Waiting for voice input";
  };

  return (
    <div className="voice-root">
      <div className="bg-decoration">
        <div className="bg-glow-1"></div>
        <div className="bg-glow-2"></div>
      </div>

      <main className="voice-main">
        <div className="studio-container">
          <div className="interaction-studio">
            <div className="studio-header">
              <div className="studio-nav">
                <button
                  type="button"
                  className="voice-back-btn"
                  onClick={() => {
                    if (onBack) return onBack();
                    if (window.history.length > 1) return window.history.back();
                    window.location.href = "/";
                  }}
                  aria-label="Go back"
                >
                  <span className="material-symbols-outlined">arrow_back</span>
                  <span>Back</span>
                </button>
                <button
                  type="button"
                  className="admin-portal-btn"
                  onClick={() => {
                    if (onAdmin) onAdmin();
                    else window.location.href = "/admin";
                  }}
                >
                  <span className="material-symbols-outlined">lock</span>
                  <span>Admin Portal</span>
                </button>
              </div>
              <div style={{ textAlign: 'center', marginTop: '10px' }}>
                <h3 className="studio-sub-title" style={{ margin: 0, fontSize: '28px', fontWeight: 'bold', color: '#1f2937' }}>Krushi Samrudhi Helpline</h3>
              </div>
              <p className="studio-subtitle" style={{ textAlign: 'center', margin: '10px auto 0 auto', maxWidth: '80%', color: '#4b5563' }}>
                Your direct connection to agricultural expertise. Select your model and start your query.
              </p>
            </div>

            <div className="mode-toggle-wrapper">
              <div className="mode-toggle-pill">
                <button
                  className={`toggle-btn ${inputMode === "voice" ? "active" : ""}`}
                  onClick={() => { setInputMode("voice"); inputModeRef.current = "voice"; resetSession(); }}
                >
                  <span className="material-symbols-outlined">mic</span>
                  Voice Mode
                </button>
                <button
                  className={`toggle-btn ${inputMode === "text" ? "active" : ""}`}
                  onClick={() => { setInputMode("text"); inputModeRef.current = "text"; resetSession(); }}
                >
                  <span className="material-symbols-outlined">keyboard</span>
                  Text Mode
                </button>
              </div>
            </div>

            <div className="studio-card">
              {inputMode === "voice" ? (
                <div className="voice-unit" style={{ animation: "fadeIn 0.4s ease-out" }}>
                  <div className="mic-orb-container">
                    <div className={`orb-pulse ${isRecording ? "active" : ""}`}></div>
                    <button
                      className="mic-orb"
                      onClick={handleStartRecording}
                      disabled={isDisabledMic}
                      aria-label="Start voice interaction"
                    >
                      <span className="material-symbols-outlined">
                        {isRecording ? "graphic_eq" : "mic"}
                      </span>
                      <div className="tap-label">Tap to Speak</div>
                    </button>
                  </div>
                  <div className="status-indicator">
                    <div className="status-control-row" style={{ display: "flex", alignItems: "center", gap: "12px", justifyContent: "center" }}>
                      <div className="status-text">{getStatusText()}</div>
                      {(isAudioPlaying || replayingIndex !== null) && (
                        <button
                          className="playback-toggle-btn"
                          onClick={togglePlayback}
                          style={{
                            background: "rgba(255,255,255,0.1)",
                            border: "1px solid rgba(255,255,255,0.2)",
                            borderRadius: "50%",
                            padding: "6px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            color: "white"
                          }}
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: "20px" }}>
                            {isPaused ? "play_circle" : "pause_circle"}
                          </span>
                        </button>
                      )}
                    </div>
                    {isRecording && (
                      <div className="audio-waves">
                        {[...Array(6)].map((_, i) => (
                          <div key={i} className="wave-bar active" style={{ animationDelay: `${i * 0.1}s` }} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-unit" style={{ animation: "fadeIn 0.4s ease-out" }}>
                  {isSessionStarted ? (
                    <div className="setting-field">
                      <label className="studio-label">Describe your query</label>
                      <div className="textarea-wrapper">
                        <textarea
                          className="studio-textarea"
                          placeholder="Ex: I want to book insemination process for my cow..."
                          value={inputText}
                          onChange={(e) => setInputText(e.target.value)}
                          disabled={isProcessing || isDone || isMissingSelection}
                          maxLength={MAX_TEXT_LENGTH}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="start-session-prompt">
                      <div className="start-icon">
                        <span className="material-symbols-outlined">chat</span>
                      </div>
                      <h3>Ready to start your conversation?</h3>
                      <p>Select your AI Model, Language and Mobile number, then click Start to begin.</p>
                    </div>
                  )}
                </div>
              )}

              <div className="input-settings-grid">
                <div className="setting-field">
                  <label className="studio-label">Mobile Number</label>
                  <div className="premium-select-wrapper">
                    <select
                      className="premium-select"
                      value={selectedMobileNumber}
                      onChange={(e) => setSelectedMobileNumber(e.target.value)}
                    >
                      <option value="">Select Your Mobile Number</option>
                      {Object.entries(MOBILE_NUMBER).map(([key]) => (
                        <option key={key} value={key}>{key}</option>
                      ))}
                    </select>
                    <span className="material-symbols-outlined select-arrow">expand_more</span>
                  </div>
                </div>

                <div className="setting-field">
                  <label className="studio-label">Village</label>
                  <div className="premium-select-wrapper">
                    <input
                      type="text"
                      className="premium-select"
                      style={{ background: "#f5f5f4", cursor: "default" }}
                      value={MOBILE_NUMBER[selectedMobileNumber] || "Not Registered Number"}
                      readOnly
                    />
                  </div>
                </div>

                <div className="setting-field">
                  <label className="studio-label">AI Model</label>
                  <div className="premium-select-wrapper">
                    <select
                      className="premium-select"
                      value={model}
                      onChange={(e) => setModel(e.target.value)}
                    >
                      <option value="">Select AI Model</option>
                      {Object.entries(MODEL_MAP).map(([key, val]) => (
                        <option key={key} value={key}>{val.name}</option>
                      ))}
                    </select>
                    <span className="material-symbols-outlined select-arrow">expand_more</span>
                  </div>
                </div>

                <div className="setting-field">
                  <label className="studio-label">Your Language</label>
                  <div className="premium-select-wrapper">
                    <select
                      className="premium-select"
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                    >
                      <option value="">Select Language</option>
                      {Object.entries(ACTIVE_LANG_MAP).map(([key, val]) => (
                        <option key={key} value={key}>{val.name}</option>
                      ))}
                    </select>
                    <span className="material-symbols-outlined select-arrow">expand_more</span>
                  </div>
                </div>
              </div>

              {inputMode === "text" && (
                <div className="studio-footer">
                  <div className="status-badges">
                    <div className="badge badge-emerald">
                      <div className="badge-dot"></div>
                      {isSessionStarted ? "Session Active" : "AI Ready"}
                    </div>
                  </div>
                  {!isSessionStarted ? (
                    <button className="process-button" onClick={handleStartSession} disabled={isStartSessionDisabled}>
                      {isProcessing ? "Starting..." : "Start"}
                      <span className="material-symbols-outlined">play_arrow</span>
                    </button>
                  ) : (
                    <button className="process-button" onClick={handleTextSubmit} disabled={isTextSubmitDisabled}>
                      {isProcessing ? "Processing..." : "Process Query"}
                      <span className="material-symbols-outlined">send</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <aside className="chat-panel-container">
            <div className="chat-panel">
              <div className="chat-header">
                <span>CONVERSATION STREAM</span>
              </div>
              <div className="chat-scroll-area" ref={chatContainerRef}>
                {chatHistory.length === 0 ? (
                  <div className="empty-chat">
                    <span className="material-symbols-outlined" style={{ fontSize: "48px", opacity: 0.2 }}>forum</span>
                    <p>No messages yet</p>
                  </div>
                ) : (
                  chatHistory.map((msg, idx) => (
                    <div key={idx} className={`chat-bubble-wrapper ${msg.from}`}>
                      <div className="chat-bubble" style={msg.from === "bot" || msg.from === "system" ? { textAlign: "justify" } : {}}>
                        {msg.text}
                        {msg.from !== "system" && (
                          <button
                            className={`replay-btn ${replayingIndex === idx ? "playing" : ""}`}
                            onClick={() => handleReplayMessage(idx)}
                          >
                            <span
                              className="material-symbols-outlined"
                              style={{ fontSize: "14px" }}
                            >
                              {replayingIndex === idx
                                ? pausedIndex === idx
                                  ? "play_arrow"
                                  : "pause"
                                : "play_circle"}
                            </span>
                            {replayingIndex === idx
                              ? pausedIndex === idx
                                ? "Resume"
                                : "Pause"
                              : "Replay"}
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
};

const SchemePage = (props) => (
  <ErrorBoundary>
    <SchemePageInner {...props} />
  </ErrorBoundary>
);

export default SchemePage;